import type { SignInRequired } from '@/lib/types/auth.types';
import { apiClient } from './client';
import type { SessionResponse } from './auth.api';
import { toSessionUser, type RawSessionUser } from '@/lib/utils/session.utils';

export interface InvitePreview {
  /** `startup_admin` (MYN-311): an MNR admin invited this person to create their startup. */
  inviteType: 'startup_member' | 'investor' | 'trusted_partner' | 'startup_admin';
  roleToAssign: 'owner' | 'admin' | 'sub_admin' | 'member';
  recipientEmail: string;
  entityName: string;
  /** Name the inviter supplied for this recipient, if any - pre-fills (still editable) Full Name on the signup form. */
  inviteeName: string | null;
  /**
   * `'login'`: the recipient already has a verified account and no active
   * membership elsewhere - log in (if not already) and click Accept, no OTP.
   * `'otp'`: everyone else - today's password-form + email-OTP flow.
   */
  acceptMethod: 'otp' | 'login';
}

/** Result of the login-based accept path - no session is returned; call
 * `refreshSession` afterward so the new membership is reflected in the JWT. */
export interface AcceptInviteAuthenticatedResult {
  /** Null for a startup admin invite: the startup does not exist until they create it. */
  entityId: string | null;
  entityName: string | null;
  role: 'owner' | 'admin' | 'sub_admin' | 'member' | null;
  /** `create_startup`: open Create Startup instead of the dashboard (MYN-311). */
  nextStep: 'create_startup' | null;
}

export interface AcceptInvitePayload {
  fullName: string;
  password: string;
  linkedinUrl?: string;
  tosAccepted: boolean;
}

/** The resume branch's result: an existing UNVERIFIED account still verifies by email code (FR-208). */
export interface AcceptInviteOtpStep {
  email: string;
  otpEmailSent: boolean;
}

/**
 * What `POST /invites/:token/accept` answers (FR-208, MYN-292): a new invitee's
 * link is the proof of the mailbox, so they get what `verify-otp` returns (a
 * session, or `signInRequired` with 2FA on); only the resume branch answers
 * with the OTP step. Told apart by `'otpEmailSent' in result`.
 */
export type AcceptInviteResult = AcceptInviteOtpStep | SessionResponse | SignInRequired;

/** Narrows an accept result to the resume branch, which still needs the emailed code. */
export function isInviteOtpStep(result: AcceptInviteResult): result is AcceptInviteOtpStep {
  return 'otpEmailSent' in result;
}

export interface VerifyInviteOtpPayload {
  code: string;
}

/** Validate an invite token and fetch the signup form's context (US-038 AC1/AC3). */
export async function validateInvite(token: string): Promise<InvitePreview> {
  const { data } = await apiClient.get<InvitePreview>(`/invites/${token}`);
  return data;
}

/**
 * Invite acceptance (US-038/US-098, FR-208): submit name/password. A new
 * invitee is created verified and gets the session (or `signInRequired`) in
 * this one call; a resumed unverified account gets the OTP step instead and
 * finishes with `verifyInviteOtp`.
 */
export async function acceptInvite(
  token: string,
  payload: AcceptInvitePayload,
): Promise<AcceptInviteResult> {
  const { data } = await apiClient.post<
    AcceptInviteOtpStep | { accessToken: string; user: RawSessionUser } | SignInRequired
  >(`/invites/${token}/accept`, payload);
  if ('otpEmailSent' in data || 'signInRequired' in data) return data;
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

/** Step 2: verify the invite-accept OTP and receive the session. */
export async function verifyInviteOtp(
  token: string,
  payload: VerifyInviteOtpPayload,
): Promise<SessionResponse | SignInRequired> {
  const { data } = await apiClient.post<{ accessToken: string; user: RawSessionUser } | SignInRequired>(
    `/invites/${token}/verify-otp`,
    payload,
  );
  // With 2FA on there is no session yet: the user signs in and sets up the app (MYN-241).
  if ('signInRequired' in data) return data;
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

/** Resend the invite-accept verification code. */
export async function resendInviteOtp(token: string): Promise<void> {
  await apiClient.post(`/invites/${token}/resend-otp`);
}

/**
 * Accept an invite using the caller's own existing, already-verified session
 * - no OTP. Only meaningful when `InvitePreview.acceptMethod === 'login'`;
 * requires a valid session (goes through the normal authenticated
 * `apiClient`, unlike the anonymous token-only calls above).
 */
export async function acceptInviteAuthenticated(
  token: string,
): Promise<AcceptInviteAuthenticatedResult> {
  const { data } = await apiClient.post<AcceptInviteAuthenticatedResult>(
    `/invites/${token}/accept-authenticated`,
  );
  return data;
}
